import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { 
  FaClipboardList, 
  FaChartPie, 
  FaUsers, 
  FaSearchLocation,
  FaMobileAlt,
  FaFileAlt,
  FaRegLightbulb,
  FaRegChartBar
} from 'react-icons/fa';

const Survey = () => {
  const surveyTypes = [
    {
      icon: <FaUsers />,
      title: "Market Research",
      description: "Comprehensive market analysis and consumer behavior studies",
      features: [
        "Consumer preferences analysis",
        "Market trend identification",
        "Competitor analysis",
        "Brand perception studies"
      ]
    },
    {
      icon: <FaClipboardList />,
      title: "Opinion Polls",
      description: "Gather public opinion on various topics and issues",
      features: [
        "Political surveys",
        "Social issue analysis",
        "Community feedback",
        "Public sentiment tracking"
      ]
    },
    {
      icon: <FaSearchLocation />,
      title: "Local Surveys",
      description: "Region-specific data collection and analysis",
      features: [
        "Geographic targeting",
        "Local market insights",
        "Community needs assessment",
        "Regional trend analysis"
      ]
    },
    {
      icon: <FaMobileAlt />,
      title: "Digital Surveys",
      description: "Online and mobile-based survey solutions",
      features: [
        "Mobile-friendly surveys",
        "Real-time data collection",
        "Social media integration",
        "Digital response tracking"
      ]
    }
  ];

  const methodologies = [
    {
      title: "Data Collection",
      points: [
        "Online surveys",
        "Phone interviews",
        "Face-to-face interviews",
        "Focus groups",
        "Mobile surveys"
      ]
    },
    {
      title: "Analysis Methods",
      points: [
        "Statistical analysis",
        "Qualitative research",
        "Cross-tabulation",
        "Trend analysis",
        "Demographic segmentation"
      ]
    },
    {
      title: "Reporting",
      points: [
        "Interactive dashboards",
        "Custom reports",
        "Real-time updates",
        "Visual presentations",
        "Executive summaries"
      ]
    }
  ];

  const benefits = [
    {
      icon: <FaRegLightbulb />,
      title: "Informed Decision Making",
      description: "Make data-driven decisions based on reliable survey insights"
    },
    {
      icon: <FaChartPie />,
      title: "Market Understanding",
      description: "Gain deep insights into market trends and consumer behavior"
    },
    {
      icon: <FaFileAlt />,
      title: "Comprehensive Reports",
      description: "Receive detailed analysis and actionable recommendations"
    },
    {
      icon: <FaRegChartBar />,
      title: "Performance Metrics",
      description: "Track and measure key performance indicators"
    }
  ];

  useEffect(() => {
    AOS.init({ duration: 1000, once: true, easing: 'ease-out' });
  }, []);

  return (
    <div className="min-h-screen pt-20 px-4 bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <div className="max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="text-center mt-24 mb-16" data-aos="fade-up">
          <div className="relative inline-block">
            <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              Survey Services
            </h1>
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full"></div>
          </div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mt-8 leading-relaxed">
            Professional survey solutions for gathering valuable insights and making data-driven decisions
          </p>
        </div>

        {/* Survey Types Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          {surveyTypes.map((type, index) => (
            <div
              key={index}
              data-aos="fade-up"
              data-aos-delay={index * 100}
              className="bg-white rounded-xl p-6 transform hover:-translate-y-2 transition-all duration-300 hover:shadow-xl border border-blue-100"
            >
              <div className="text-blue-600 mb-6 bg-white p-4 rounded-lg inline-block shadow-md text-3xl flex justify-center">
                {type.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2 text-center">{type.title}</h3>
              <p className="text-gray-600 mb-4 text-center">{type.description}</p>
              <ul className="space-y-2">
                {type.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center text-gray-600">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Methodology Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-16" data-aos="fade-up">
          <h2 className="text-3xl font-bold text-center mb-12">Our Survey Methodology</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {methodologies.map((method, index) => (
              <div key={index} className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-xl font-semibold mb-4">{method.title}</h3>
                <ul className="space-y-3">
                  {method.points.map((point, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl shadow-lg p-8 mb-16" data-aos="fade-up">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 text-center shadow-md hover:shadow-lg transition-shadow">
                <div className="text-blue-600 text-3xl mb-4 flex justify-center">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div 
          className="relative overflow-hidden rounded-2xl mb-16"
          data-aos="fade-up"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-90"></div>
          <div className="relative z-10 p-12 text-center text-white">
            <h2 className="text-3xl font-bold mb-6">Ready to Start Your Survey Project?</h2>
            <p className="text-xl mb-8 opacity-90">Let us help you gather the insights you need</p>
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-opacity-90 transition-all duration-300 shadow-lg">
              Get Started
            </button>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 my-16" data-aos="fade-up">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: "1000+", label: "Surveys Conducted" },
              { number: "50K+", label: "Respondents Reached" },
              { number: "95%", label: "Client Satisfaction" },
              { number: "24/7", label: "Support Available" }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Survey;


